import React from 'react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-sm font-medium"
          >
            ← Back to Home
          </Link>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg px-8 py-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            VeriVox AI Trust Page
          </h1>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
            At VeriVox AI, we understand how important your data is. Our
            Toronto-based information security and compliance team uses expert
            oversight, advanced AI technology, and global best practices to
            ensure that your data remains protected at all times.
          </p>

          <div className="prose prose-indigo dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Infrastructure
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                VeriVox AI leverages secure Canadian and global cloud
                infrastructure to ensure data safety and uptime.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We use AWS and other compliant providers for hosting with
                  multi-region redundancy and strict access control.
                </li>
                <li>
                  All cloud operations are secured with SSO, MFA, and logged
                  activity monitoring.
                </li>
                <li>
                  Continuous vulnerability scanning and cloud security posture
                  management ensure compliance and fast remediation of issues.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Application Security
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  All data transmission is encrypted using HTTPS and TLS
                  protocols; stored data uses AES-256 encryption.
                </li>
                <li>
                  Access control includes two-factor authentication, role-based
                  permissions, and strict account management.
                </li>
                <li>
                  Regular independent penetration tests are conducted annually
                  by certified cybersecurity experts.
                </li>
                <li>
                  All code is reviewed under a Secure Software Development
                  Lifecycle (SSDLC) following OWASP standards.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Corporate Security
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We apply single sign-on (SSO) across corporate systems with
                  enforced two-factor authentication.
                </li>
                <li>
                  All endpoints are encrypted and centrally managed, with the
                  ability to remotely lock or wipe devices.
                </li>
                <li>
                  Our systems include next-generation antivirus (NGAV) and EDR
                  with 24/7 managed threat detection.
                </li>
                <li>
                  Vendor risk management and security reviews ensure that all
                  third-party partners maintain compliance.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Human Resources
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  All VeriVox AI employees complete annual information security
                  and privacy training.
                </li>
                <li>
                  We maintain a Security Champions program to reinforce
                  proactive cybersecurity awareness.
                </li>
                <li>
                  Background and identity checks are conducted as allowed by law
                  for new hires handling sensitive data.
                </li>
                <li>
                  Access rights follow the principle of least privilege and are
                  reviewed regularly.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Privacy & Data Protection
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We integrate privacy by design and default in all our products
                  and processes.
                </li>
                <li>
                  Our{' '}
                  <Link
                    to="/privacy"
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                  >
                    Privacy Policy
                  </Link>{' '}
                  clearly explains how your data is collected, used, and stored.
                </li>
                <li>
                  We sign Data Processing Agreements (DPAs) with customers and
                  third parties to ensure lawful data processing.
                </li>
                <li>
                  Data transfers comply with Canadian PIPEDA and international
                  standards including Standard Contractual Clauses (SCCs).
                </li>
                <li>
                  VeriVox AI does not allow government access to personal data
                  unless legally compelled and reviewed by our legal team.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Governance & Compliance
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  Our Information Security Management System (ISMS) follows ISO
                  27001:2022 and SOC 2 frameworks.
                </li>
                <li>
                  Risk assessments are performed regularly and reviewed by
                  executive leadership.
                </li>
                <li>
                  Information security policies are reviewed annually and
                  communicated company-wide.
                </li>
                <li>
                  Internal audits and continuous improvement programs ensure
                  accountability and compliance.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Contact
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                For any security or privacy-related inquiries, contact our team:
              </p>
              <ul className="list-none space-y-1 text-gray-700 dark:text-gray-300 ml-4 mt-2">
                <li>
                  Email:{' '}
                  <a
                    href="mailto:security@verivox.ai"
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                  >
                    security@verivox.ai
                  </a>
                </li>
                <li>Phone: +1 (416) XXX-XXXX</li>
              </ul>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                © VeriVox AI 2025 - All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
